import React, { useMemo } from "react";
import { ReviewQuestionWrapper } from "../shared/ReviewQuestionWrapper";
import { MultipleCorrectOptionItem } from "../shared/MultipleCorrectOptionItem";
import { normalizeOptionId } from "../shared/reviewUtils";
import {
  useMultipleCorrectAnswerData,
  useNormalizedUserIds,
  useNormalizedCorrectIds,
} from "../../../../../hooks/useMultipleCorrectAnswerData";

const MultipleCorrectAnswerReview = ({
  question,
  userAnswer = [],
  correctAnswer = [],
  explanation = "",
  recap = "",
  annotations = [],
  reportProps = null,
}) => {
  // Transform backend data
  const transformedQuestion = useMultipleCorrectAnswerData(question);

  // CRITICAL: userAnswer sudah di-pass dari ReviewContent.jsx yang mengambil dari localStorage
  // Langsung pakai userAnswer dari prop, tidak perlu ambil dari localStorage lagi
  const finalUserAnswer = useMemo(() => {
    return userAnswer || [];
  }, [userAnswer]);

  // Fallback untuk text array (seharusnya tidak terjadi)
  const normalizedUserIdsFromText = useNormalizedUserIds(
    finalUserAnswer,
    question.choices
  );
  const normalizedCorrectIds = useNormalizedCorrectIds(correctAnswer);

  // CRITICAL: userAnswer HARUS dalam format [0, 3] (array of idx numbers) dari localStorage
  // Langsung pakai jika sudah numbers, JANGAN transform ke text
  const finalNormalizedUserIds = useMemo(() => {
    if (!finalUserAnswer) return [];
    if (!Array.isArray(finalUserAnswer)) {
      return typeof finalUserAnswer === "number" ? [finalUserAnswer] : [];
    }
    if (finalUserAnswer.length === 0) return [];

    // Jika semua adalah numbers, langsung pakai
    if (finalUserAnswer.every((item) => typeof item === "number")) {
      return finalUserAnswer;
    }

    // Jika ada string yang bisa di-convert ke number, convert
    const converted = finalUserAnswer
      .map((id) => (typeof id === "number" ? id : Number(id)))
      .filter((id) => !isNaN(id));

    if (converted.length === finalUserAnswer.length) {
      return converted;
    }

    // Fallback: jika text array, convert dengan match choices
    return normalizedUserIdsFromText;
  }, [finalUserAnswer, normalizedUserIdsFromText]);

  // Get correct IDs from option.isCorrect (source of truth)
  const finalCorrectIds = useMemo(() => {
    const fromOptions = transformedQuestion.options
      .filter((opt) => opt.isCorrect)
      .map((opt) => (typeof opt.id === "number" ? opt.id : Number(opt.id)));

    return fromOptions.length > 0 ? fromOptions : normalizedCorrectIds;
  }, [transformedQuestion.options, normalizedCorrectIds]);

  return (
    <ReviewQuestionWrapper
      questionType="Multiple Correct Answer"
      question={question}
      explanation={explanation}
      recap={recap}
      annotations={annotations}
      reportProps={reportProps}
    >
      <div
        className="text-base text-[var(--text-primary)] leading-relaxed"
        dangerouslySetInnerHTML={{ __html: transformedQuestion.text }}
      />

      <div className="space-y-2">
        {Array.isArray(transformedQuestion.options) &&
        transformedQuestion.options.length > 0 ? (
          transformedQuestion.options.map((option) => {
            // Get option ID (should be the idx from choices)
            const optionIdx = normalizeOptionId(option.id);

            // Check if this option was selected by user
            const isUserSelection = finalNormalizedUserIds.includes(optionIdx);

            // Check if this option is the correct answer
            const isCorrectAnswer = finalCorrectIds.includes(optionIdx);

            return (
              <MultipleCorrectOptionItem
                key={option.id}
                option={option}
                isUserSelection={isUserSelection}
                isCorrectAnswer={isCorrectAnswer}
              />
            );
          })
        ) : (
          <div className="p-4 rounded-lg border bg-yellow-100">
            <p className="text-yellow-800">No options available</p>
          </div>
        )}
      </div>
    </ReviewQuestionWrapper>
  );
};

export default MultipleCorrectAnswerReview;
